import { formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import { parseEther } from 'ethers/lib/utils'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import { EST_TRADE_TX_COST } from 'constants/defaults'
import { withdrawFundsFromOneClick } from 'state/futures/actions'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import {
	BalanceContainer,
	BalanceText,
	DepositInputContainer,
	DepositInputMain,
} from '../../ManageSmartMarginModal'
import useAbstractionInfo from '../hooks/useAbstractionInfo'

import AbstractionWalletInfo from './AbstractionWalletInfo'

const WithdrawTab: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const { balance } = useAbstractionInfo()
	const txProcessing = useAppSelector(selectSubmittingFuturesTx)

	const [value, setValue] = useState<string>('')

	const withdrawEther = async () => {
		const ethValue = parseEther(value)
		if (ethValue.gt(parseEther(balance))) {
			return
		}
		dispatch(withdrawFundsFromOneClick(ethValue))
	}

	const formattedBalance = useMemo(
		() => formatNumber(balance, { suggestDecimals: true }),
		[balance]
	)

	const handleSetMax = useCallback(() => {
		const max = (parseFloat(formattedBalance) - EST_TRADE_TX_COST).toString()
		setValue(max)
	}, [formattedBalance])

	const isDisabled = useMemo(() => {
		return !value || txProcessing || wei(value || '0').gte(balance)
	}, [value, balance, txProcessing])

	return (
		<section>
			<WithdrawTitle>{t('futures.one-click-trading.withdraw-title')}</WithdrawTitle>
			<AbstractionWalletInfo />
			<Spacer height={20} />
			<BalanceContainer>
				<BalanceText>{t('futures.market.trade.margin.modal.deposit.amount')}</BalanceText>
				<BalanceText>
					{t('futures.one-click-trading.onboard.balance')}{' '}
					<NumericValue size="small" as="span" onClick={handleSetMax} style={{ cursor: 'pointer' }}>
						{formattedBalance}
					</NumericValue>
				</BalanceText>
			</BalanceContainer>
			<DepositInputContainer>
				<DepositInputMain placeholder="0.00" value={value} onChange={(_, v) => setValue(v)} />
				<Body>ETH</Body>
			</DepositInputContainer>
			<Spacer height={20} />
			<StyledButton loading={txProcessing} noOutline onClick={withdrawEther} disabled={isDisabled}>
				{t('futures.one-click-trading.withdraw')}
			</StyledButton>
		</section>
	)
}

const WithdrawTitle = styled(Body)`
	margin: 0 0 10px 0;
	color: ${(props) => props.theme.colors.selectedTheme.text.label};
`

const StyledButton = styled(TransactionButton)`
	height: 50px;
	width: 100%;
`

export default WithdrawTab
